import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Label } from "./ui/label";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "./ui/form";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "../components/ui/command";
import { z } from "zod";
import { useToast } from "./ui/use-toast";
import { ClipLoader } from "react-spinners";
import { cn } from "../lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import { Check, ChevronsUpDown } from "lucide-react";
import { useCreateUser } from "../migration-api/api-hooks/users";
import {
  useCheckClientLocations,
  useGetAllClients,
  useGetClientById,
} from "../migration-api/api-hooks/clients";
import { useGetAllRoles } from "../migration-api/api-hooks/roles";
import { ScrollArea } from "./ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import useAuth from "../hooks/useAuth";

function NewUser() {
  const [selectedEmail, setSelectedEmail] = useState("");
  const [selectedClientId, setSelectedClientId] = useState("");
  const [clientLocations, setClientLocations] = useState([]);

  const { toast } = useToast();
  const { auth } = useAuth();

  // Determine user role
  const isSuperAdmin = auth?.user?.role === "SUPER_ADMIN";
  const isAdmin = auth?.user?.role !== "SUPER_ADMIN";
  const clientId = auth?.user?.clientId;

  const formSchema = z.object({
    email: z.string().email({
      message: "Only valid email is allowed",
    }),
    location: z.string({
      message: "Please select a location",
    }),
    clientId: z.string({
      message: "Please select a client",
    }),
    firstName: z.string({
      message: "This field is required",
    }),
    lastName: z.string({
      message: "This field is required",
    }),
    roleId: z.string({
      message: "Please select a role",
    }),
  });

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      lastName: "",
      firstName: "",
      clientId: "",
      location: "",
      roleId: "",
    },
  });

  // Fetch all clients
  const { data: clientsData, isLoading: isLoadingClients } = useGetAllClients();
  const allClients = clientsData?.data || [];

  // Fetch a client
  const { data: singleClientResponse, isLoading: isLoadingSingleClient } =
    useGetClientById(clientId, {
      enabled: isAdmin && !!clientId,
    });
  const client = singleClientResponse?.data;

  // Derived a consistent clients array
  const getClientsForDisplay = () => {
    if (isSuperAdmin) {
      return allClients;
    }
    // For admins, return array with their single client (only if loaded)
    return client && client.id ? [client] : [];
  };

  const clientsForDisplay = getClientsForDisplay();
  const isLoadingClientData = isSuperAdmin
    ? isLoadingClients
    : isLoadingSingleClient;

  // Fetch all roles
  const { data: rolesData, isLoading: isLoadingRoles } = useGetAllRoles();
  const allRoles = rolesData?.data || [];

  // Check/fetch client locations
  const { mutate: checkClientLocations, isLoading: isLoadingLocations } =
    useCheckClientLocations({
      onSuccess: (data) => {
        const locations = data?.data?.locations || [];
        setClientLocations(locations);
      },
      onError: (error) => {
        console.error("Error loading locations:", error);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to load locations",
        });
        setClientLocations([]);
      },
    });

  const { createUser, isLoading } = useCreateUser({
    onSuccess: () => {
      toast({
        variant: "success",
        title: "Success",
        description: "User Successfully Registered",
      });
      // Reset form
      form.reset();
      setClientLocations([]);
      setSelectedClientId("");
      setSelectedEmail("");
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error?.response?.data?.message || "Error Creating User",
      });
    },
  });


  // useEffect to auto-select the client for admins
useEffect(() => {
  if (isAdmin && client && client.id && !form.getValues('clientId')) {
    handleClientSelect(client.id.toString(), client.email);
  }
}, [client, isAdmin]);


  function onSubmit(data) {
    // console.log('Form data before submit:', data)

    // Prepare payload - convert IDs to numbers if backend expects numbers
    const payload = {
      ...data,
      clientId: parseInt(data.clientId),
    };

    // console.log('Payload to send:', payload)
    createUser(payload);
  }

  const handleClientSelect = (clientId, clientEmail) => {
    // console.log('Client selected, ID:', clientId)

    // Update form values - use clientId (matching schema)
    form.setValue("clientId", clientId);
    form.setValue("location", ""); // Clear location when client changes

    // Update state
    setSelectedClientId(clientId);
    setSelectedEmail(clientEmail);
    setClientLocations([]); // Clear previous locations

    // Fetch locations for the selected client
    checkClientLocations({ client_email: clientEmail });
  };

  return (
    <div className="h-screen">
      <ScrollArea className="h-[800px] w-full px-4">
        <div className="pb-14">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <Label>First Name</Label>
                    <FormControl>
                      <Input
                        type="text"
                        placeholder={"First name"}
                        autoComplete="off"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <Label>Last Name</Label>
                    <FormControl>
                      <Input
                        type="text"
                        placeholder={"Last name"}
                        autoComplete="off"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <Label>Email Address</Label>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder={"johndoe@mail.com"}
                        autoComplete="off"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Client Selection */}
              <FormField
                control={form.control}
                name="clientId"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Clients</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant="outline"
                            role="combobox"
                            className={cn(
                              "w-[100%] justify-between",
                              !field.value && "text-muted-foreground",
                            )}
                            disabled={isLoadingClientData}
                          >
                            {isLoadingClientData
                              ? "Loading clients..."
                              : field.value
                                ? clientsForDisplay.find(
                                    (c) => c.id.toString() === field.value,
                                  )?.clientName
                                : "Select client"}
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-[100%] p-0">
                        <Command>
                          <CommandInput placeholder="Search client..." />
                          <CommandEmpty>No client found.</CommandEmpty>
                          <ScrollArea className="max-h-[200px]">
                            <CommandGroup>
                              {clientsForDisplay.map((client) => (
                                <CommandItem
                                  value={client.clientName}
                                  key={client.id}
                                  onSelect={() =>
                                    handleClientSelect(
                                      client.id.toString(),
                                      client.email,
                                    )
                                  }
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-4 w-4",
                                      client.id.toString() === field.value
                                        ? "opacity-100"
                                        : "opacity-0",
                                    )}
                                  />
                                  {client.clientName}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </ScrollArea>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="location"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Location</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant="outline"
                            role="combobox"
                            className={cn(
                              "w-[100%] justify-between",
                              !field.value && "text-muted-foreground",
                            )}
                            disabled={!selectedClientId || isLoadingLocations}
                          >
                            {isLoadingLocations
                              ? "Loading locations..."
                              : field.value && clientLocations.length > 0
                                ? clientLocations.find(
                                    (location) => location?.gln === field.value,
                                  )?.address
                                : selectedClientId
                                  ? "Select location"
                                  : "Select a client first"}
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-[100%] p-0">
                        <Command>
                          <CommandInput placeholder="Search location.." />
                          <CommandEmpty>
                            {selectedClientId
                              ? "No locations found for this client"
                              : "Select a client first"}
                          </CommandEmpty>
                          <ScrollArea className="max-h-[200px]">
                            <CommandGroup>
                              {clientLocations.map((location) => (
                                <CommandItem
                                  value={location.address}
                                  key={location.gln}
                                  onSelect={() => {
                                    form.setValue("location", location.gln);
                                  }}
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-4 w-4",
                                      location.gln === field.value
                                        ? "opacity-100"
                                        : "opacity-0",
                                    )}
                                  />
                                  {location.address}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </ScrollArea>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Role Selection - Using Select dropdown */}
              <FormField
                control={form.control}
                name="roleId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Select Role</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value}
                      disabled={isLoadingRoles}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue
                            placeholder={
                              isLoadingRoles
                                ? "Loading roles..."
                                : "Select a role"
                            }
                          />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className="max-h-[300px]">
                        {allRoles.map((role) => (
                          <SelectItem key={role.id} value={role.id.toString()}>
                            {role.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button type="submit" disabled={isLoading}>
                {isLoading ? (
                  <span className="mx-[20px]">
                    <ClipLoader color="primary" size={20} />
                  </span>
                ) : (
                  <span>Submit</span>
                )}
              </Button>
            </form>
          </Form>
        </div>
      </ScrollArea>
    </div>
  );
}

export default NewUser;
